import React from 'react';
import { Box } from '@mui/material';

interface Silo2DProps {
  radius?: number;
  cylinderHeight?: number;
  unit?: string;
  color?: string;
  strokeColor?: string;
}

export const Silo2D: React.FC<Silo2DProps> = ({
  radius = 5,
  cylinderHeight = 10,
  unit = 'm',
  color = '#FFFACD',
  strokeColor = '#9c27b0'
}) => {
  const cx = 50;
  const cy = 55;
  
  const maxDim = Math.max(radius * 2, cylinderHeight + radius);
  const scale = 45 / maxDim;
  
  const R = radius * scale;
  const H = cylinderHeight * scale;
  
  const r_proj = R * Math.sqrt(1.5);
  const rx = r_proj;
  const ry = R * Math.SQRT1_2;
  
  const bottomY = cy + H / 2;
  const topY = cy - H / 2;

  // Paths
  const backBottomPath = `M ${cx - rx} ${bottomY} A ${rx} ${ry} 0 0 1 ${cx + rx} ${bottomY}`;
  const frontBottomPath = `M ${cx - rx} ${bottomY} A ${rx} ${ry} 0 0 0 ${cx + rx} ${bottomY}`;
  
  const domePath = `M ${cx - rx} ${topY} A ${r_proj} ${r_proj} 0 0 1 ${cx + rx} ${topY}`;
  
  const bodyPath = `
    M ${cx - rx} ${topY}
    L ${cx - rx} ${bottomY}
    A ${rx} ${ry} 0 0 0 ${cx + rx} ${bottomY}
    L ${cx + rx} ${topY}
    A ${r_proj} ${r_proj} 0 0 0 ${cx - rx} ${topY}
    Z
  `;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        <defs>
          <linearGradient id="siloGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity={0.7} />
            <stop offset="50%" stopColor={color} stopOpacity={0.85} />
            <stop offset="100%" stopColor={color} stopOpacity={1.0} />
          </linearGradient>
        </defs>

        <path d={backBottomPath} fill="none" stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.5} />
        
        {/* Separation line for top of cylinder */}
        <ellipse cx={cx} cy={topY} rx={rx} ry={ry} fill="none" stroke={strokeColor} strokeWidth={1} strokeDasharray="2,2" opacity={0.4} />

        <path d={bodyPath} fill="url(#siloGrad)" />

        <path d={`M ${cx - rx} ${topY} L ${cx - rx} ${bottomY}`} fill="none" stroke={strokeColor} strokeWidth={1.5} />
        <path d={`M ${cx + rx} ${topY} L ${cx + rx} ${bottomY}`} fill="none" stroke={strokeColor} strokeWidth={1.5} />
        <path d={frontBottomPath} fill="none" stroke={strokeColor} strokeWidth={1.5} />
        <path d={domePath} fill="none" stroke={strokeColor} strokeWidth={1.5} />

        {/* Labels */}
        <circle cx={cx} cy={bottomY} r={1.2} fill={strokeColor} />
        <line x1={cx} y1={bottomY} x2={cx + rx} y2={bottomY} stroke={strokeColor} strokeWidth={1} strokeDasharray="1,2" opacity={0.6} />
        <text x={cx + rx / 2} y={bottomY - 2} fontSize={3.5} fill="#374151" textAnchor="middle" fontWeight={600}>r = {radius} {unit}</text>

        <text x={cx - rx - 4} y={(topY + bottomY) / 2 + 1.5} fontSize={3.5} fill="#374151" textAnchor="end" fontWeight={600}>h = {cylinderHeight} {unit}</text>
      </svg>
    </Box>
  );
};

export default Silo2D;
